import type { ChangeEvent, ComponentProps, MouseEvent } from 'react';
import { cn } from '../../../common/functions/cn';

export const Checkbox = ({
  className,
  readOnly,
  onChange,
  onClick,
  ...props
}: Omit<ComponentProps<'input'>, 'type'>) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (readOnly) {
      return;
    }
    onChange?.(event);
  };

  // HTML `readonly` doesn't stop a checkbox toggling. Blocking the click keeps it focusable, unlike `disabled`.
  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    if (readOnly) {
      event.preventDefault();
    }
    onClick?.(event);
  };

  return (
    <input
      type="checkbox"
      data-slot="checkbox"
      readOnly={readOnly}
      aria-readonly={readOnly || undefined}
      onChange={handleChange}
      onClick={handleClick}
      className={cn(
        'peer relative grid size-4 shrink-0 cursor-pointer appearance-none place-content-center',
        'rounded-[4px] border border-input bg-background outline-none transition-colors focus-visible:outline-hidden',
        'before:absolute before:-inset-2',
        'after:mask-contain after:mask-center after:mask-no-repeat after:hidden after:size-3.5 after:bg-primary-foreground',
        'after:mask-[url(data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20viewBox=%270%200%2024%2024%27%20fill=%27none%27%20stroke=%27black%27%20stroke-width=%272%27%20stroke-linecap=%27round%27%20stroke-linejoin=%27round%27%3E%3Cpath%20d=%27M20%206%209%2017l-5-5%27/%3E%3C/svg%3E)]',
        'checked:border-primary checked:bg-primary checked:after:block',
        'forced-colors:after:border-7',
        'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'aria-invalid:border-error aria-invalid:ring-2 aria-invalid:ring-error/20',
        'aria-invalid:focus-visible:border-error aria-invalid:focus-visible:ring-error/60',
        'disabled:cursor-not-allowed disabled:opacity-50',
        readOnly && 'cursor-not-allowed',
        className
      )}
      {...props}
    />
  );
};
